/*
 * Project: Dynamedics Portal Web
 * Created Date: Wednesday August 10th 2022
 * Author: KasunSKarunasekara
 * -----
 * Last Modified: Wednesday August 10th 2022 12:44:04 am
 * Modified By: KasunSKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2022 Mitra Sparks
 * -----
 * HISTORY:
 */

import { createSlice } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';
import { INTEGRATED_REPORT_STATUS } from 'src/utils/constants';
import { checkUndefinedOrNull } from 'src/utils/functions';
// utils

import axios from '../../utils/axios';
const ACCESS_TOKEN = 'accessToken';
const EDIT = 'edit';
const REVIEW = 'review';
const GENERATE = 'generate';

const initialState = {
  isLoading: false,
  error: false,
  reportData: [],
  frameworksData: [],
  prefilledRequirements: [],
  manifestData: {},
  reportManifestData: {},
  reportOlderDetails: {},
  status: '',
  isPermitted: true,
  isIntegratedReportLoading: false,
  initialIntegratedReport: {
    reportName: '',
    frameworkId: 0,
    frameWork: '',
    reportPeriod: '',
    reportStatus: '',
    period: {
      label: ''
    },
    introduction: {
      body: '<p></p>',
      reviewComment: {
        reviewer: 0,
        body: ''
      }
    },
    topics: [],
    standards: [],
    requirements: [],
    modifiedRequirements: [],
    reviewComment: {
      reviewer: 0,
      body: ''
    },
    reviewers: []
  }
};

const slice = createSlice({
  name: 'integratedReports',
  initialState,
  reducers: {
    //START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    startManifestLoading(state) {
      state.isManifestLoading = true;
    },

    //HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isIntegratedReportLoading = false;
      state.error = action.payload;
    },
    //START INTEGRATED REPORT LOADING
    startIntegratedReportLoading(state) {
      state.isIntegratedReportLoading = true;
    },
    // GET REPORT REVIEWERS
    getReviewsSuccess(state, action) {
      state.reviewers = action.payload;
    },

    //GET INTEGRATED REPORTS
    getIntegratedReportsSuccess(state, action) {
      state.isLoading = false;
      state.isIntegratedReportLoading = false;
      state.reportData = action.payload;
    },

    // GET EDIT INTEGRATED REPORT
    getEditIntegratedReportSuccess(state, action) {
      state.isLoading = false;
      state.singleIntegratedReport = action.payload;
      state.isReviewMode = false;
      state.isGenerateMode = false;
      state.isEditMode = true;
    },

    // GET REVIEW INTEGRATED REPORT
    getReviewIntegratedReportSuccess(state, action) {
      state.isLoading = false;
      state.singleIntegratedReport = action.payload;
      state.isEditMode = false;
      state.isGenerateMode = false;
      state.isReviewMode = true;
    },

    // GET PREVIEW INTEGRATED REPORT
    getGenerateIntegratedReportSuccess(state, action) {
      state.isLoading = false;
      state.singleIntegratedReport = action.payload;
      state.isEditMode = false;
      state.isReviewMode = false;
      state.isGenerateMode = true;
    },

    //DELETE INTEGRATE REPORTS
    deleteIntegratedReportsSuccess(state, action) {
      state.isLoading = false;
    },

    //SET REPORT ACTION PERMISSIONS
    setReportAccessPermissions(state, action) {
      const { actionType, reportData } = action.payload;
      const accessToken = localStorage.getItem('accessToken');
      if (!checkUndefinedOrNull(accessToken)) {
        const { preferred_username } = jwtDecode(accessToken);

        if (actionType === EDIT) {
          if (preferred_username !== reportData.createdBy?.username) {
            state.isPermitted = false;
          } else {
            state.isPermitted = true;
          }
        } else if (actionType === REVIEW) {
          if (preferred_username !== reportData?.reviewers[0]?.username) {
            state.isPermitted = false;
          } else {
            state.isPermitted = true;
          }
        } else if (
          actionType === GENERATE &&
          reportData.reportStatus !==
            INTEGRATED_REPORT_STATUS.APPROVED.HASH_CODE
        ) {
          if (
            preferred_username !== reportData?.reviewers[0]?.username &&
            preferred_username !== reportData.createdBy?.username
          ) {
            state.isPermitted = false;
          } else {
            state.isPermitted = true;
          }
        }
      }
    },

    //SET REPORT OLDER DETAILS FOR COMPARISONS
    setReportOlderDetails(state, action) {
      state.reportOlderDetails = {
        reportName: action.payload.reportName,
        reportPeriod: action.payload.period?.label,
        reportIntroduction: action.payload.introduction?.body
      };
    },

    // GET FRAMEWORKS
    getFrameworksSuccess(state, action) {
      state.isLoading = false;
      state.frameworksData = action.payload;
    },

    // GET FRAMEWORK MANIFEST
    getFrameworkManifestSuccess(state, action) {
      state.isManifestLoading = false;
      state.manifestData = action.payload;
    },

    //START PREFILLED LOADING
    startPrefilledLoading(state) {
      state.isPrefilledLoading = true;
    },

    // GET FRAMEWORK PREFILLED VALUES
    getPrefilledValuesSuccess(state, action) {
      state.isPrefilledLoading = false;
      state.prefilledRequirements = action.payload;
    },

    getPrefilledValuesUnsuccess(state, action) {
      state.isPrefilledLoading = false;
    },

    //SET INITIAL INTEGRATED REPORT
    setIntialIntegratedReportSuccess(state, action) {
      state.isLoading = false;
      state.singleIntegratedReport = state.initialIntegratedReport;
      state.isEditMode = false;
      state.isReviewMode = false;
      state.isGenerateMode = false;
    },
    //GET REPORT PREVIEW
    getReportPreviewSuccess(state, action) {
      state.isLoading = false;
      state.reportPreview = action.payload;
    },

    // SET REPORT DETAILS
    setReportDetails(state, action) {
      const {
        frameworkId = 0,
        reportTopics = [],
        reportName = '',
        reportPeriod = ''
      } = action.payload;

      state.reportManifestData = {
        ...state.reportManifestData,
        frameworkId: frameworkId,
        reportTopics: reportTopics,
        reportName: reportName,
        reportPeriod: reportPeriod
      };
    },
    // SET REPORT INTRO
    setReportIntro(state, action) {
      const {
        reportIntroduction = '',
        introductionComment = ''
      } = action.payload;

      state.reportManifestData = {
        ...state.reportManifestData,
        reportIntroduction: reportIntroduction,
        introductionComment: introductionComment
      };
    },
    // SET REPORT ANSWERS
    setReportStandards(state, action) {
      const {
        chosenStandards = [],
        reportAnswers = [],
        disclosureComment = ''
      } = action.payload;

      state.reportManifestData = {
        ...state.reportManifestData,
        standards: chosenStandards,
        requirements: reportAnswers,
        disclosureComment: disclosureComment
      };
    },
    // SET REPORT Reviewers
    setReportReviewers(state, action) {
      const { reportReviewers = [] } = action.payload;

      state.reportManifestData = {
        ...state.reportManifestData,
        reportReviewers: reportReviewers
      };
    },

    //reset reportPreview
    setReportPreview(state, action) {
      state.reportPreview = '';
    }
  }
});

//reducer
export default slice.reducer;

// Actions
export const {
  setReportDetails,
  setReportIntro,
  setReportStandards,
  setReportReviewers,
  setReportPreview,
  setReportOlderDetails
} = slice.actions;

// ----------------------------------------------------------------------

export function getIntegratedReports() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/intergrated-reports/reports');
      dispatch(slice.actions.getIntegratedReportsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function setInitialIntegratedReport() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.setIntialIntegratedReportSuccess());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// ----------------------------------------------------------------------

export function getEditIntegratedReport(integratedReportId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/intergrated-reports/report/${integratedReportId}`
      );
      dispatch(
        slice.actions.getEditIntegratedReportSuccess(response.data.data)
      );
      dispatch(slice.actions.setReportOlderDetails(response.data.data));
      dispatch(
        slice.actions.setReportAccessPermissions({
          actionType: EDIT,
          reportData: response.data.data
        })
      );
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getReviewIntegratedReport(integratedReportId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/intergrated-reports/report/${integratedReportId}`
      );
      dispatch(
        slice.actions.getReviewIntegratedReportSuccess(response.data.data)
      );
      dispatch(slice.actions.setReportOlderDetails(response.data.data));
      dispatch(
        slice.actions.setReportAccessPermissions({
          actionType: REVIEW,
          reportData: response.data.data
        })
      );
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getGenerateIntegratedReport(integratedReportId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `intergrated-reports/report/${integratedReportId}`
      );
      dispatch(
        slice.actions.getGenerateIntegratedReportSuccess(response.data.data)
      );
      dispatch(slice.actions.setReportOlderDetails(response.data.data));
      dispatch(
        slice.actions.setReportAccessPermissions({
          actionType: GENERATE,
          reportData: response.data.data
        })
      );
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteIntegratedReport(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.delete(`/intergrated-reports/report/${id}`);
      dispatch(slice.actions.deleteIntegratedReportsSuccess({ id }));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

// ----------------------------------------------------------------------

export function getFrameworks() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.get(`/intergrated-reports/frameworks`);
      dispatch(slice.actions.getFrameworksSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getFrameworkManifest(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startManifestLoading());
      const response = await axios.get(
        `/intergrated-reports/framework-manifest/${id}`
      );
      dispatch(slice.actions.getFrameworkManifestSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getFrameworkPrefilledRequirements(data, frameworkId) {
  const timeRange = data.reportPeriod || 'All time';
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startPrefilledLoading());
      const response = await axios.get(
        `/intergrated-reports/prefilled-requirements/${frameworkId}?timeRange=${timeRange}`
      );
      dispatch(slice.actions.getPrefilledValuesSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.getPrefilledValuesUnsuccess(error));
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export async function createIntegratedReport(data) {
  let integratedReport = { ...data };
  let reportId = integratedReport.id;
  try {
    const response = await axios.put(`/integrated-reports/report/${reportId}`, {
      ...integratedReport
    });
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export async function createCustomWasteReport(data) {
  let wasteReport = { ...data };
  try {
    const response = await axios.post(
      `/intergrated-reports/waste/generatedReport`,
      {
        ...wasteReport
      }
    );
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export async function updateIntegratedReport(data, reportFile) {
  let integratedReport = {
    reportData: { ...data },
    reportFile: { data: reportFile }
  };
  let reportId = integratedReport.reportData.id;
  try {
    const response = await axios.put(
      `/intergrated-reports/report/${reportId}`,
      {
        ...integratedReport
      }
    );
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export function getReportReviewers() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/intergrated-reports/reviewers');
      dispatch(slice.actions.getReviewsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getPreviewReport(data, shouldRegenerateReport, id) {
  let integratedReport = {
    reportData: { ...data },
    shouldRegenerateReport: shouldRegenerateReport
  };
  let reportId = id || 0;

  return async (dispatch) => {
    try {
      const response = await axios.post(
        `intergrated-reports/generatedReport/${reportId}`,
        {
          ...integratedReport
        }
      );
      dispatch(slice.actions.getReportPreviewSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
